import { createAsyncThunk } from "@reduxjs/toolkit";
import { $apiV1, $apiV2 } from "../../config/api";
import { getUserProfile, invalidateUserProfile } from "../../helpers/userProfile";

const normalizeRoles = (payload) => {
  if (Array.isArray(payload?.roles) && payload.roles.length > 0) {
    return payload.roles.filter(Boolean);
  }
  if (payload?.user_type) {
    return [payload.user_type];
  }
  return [];
};

const persistAuthContext = (payload) => {
  if (payload?.user_type != null) {
    localStorage.setItem("user_type", payload.user_type);
  } else {
    localStorage.removeItem("user_type");
  }
  const roles = normalizeRoles(payload);
  if (roles.length > 0) {
    localStorage.setItem("user_roles", JSON.stringify(roles));
  } else {
    localStorage.removeItem("user_roles");
  }
  if (payload?.mapped_player_id != null) {
    localStorage.setItem("mapped_player_id", String(payload.mapped_player_id));
  } else {
    localStorage.removeItem("mapped_player_id");
  }
  // A fresh login/registration resets any admin "view-as" selection (C1).
  localStorage.removeItem("view_as_player_id");
  if (payload?.organization_id != null) {
    localStorage.setItem("organization_id", String(payload.organization_id));
  } else {
    localStorage.removeItem("organization_id");
  }
  if (payload?.organization_kind) {
    localStorage.setItem("organization_kind", String(payload.organization_kind));
  } else {
    localStorage.removeItem("organization_kind");
  }
  if (payload?.subscription != null) {
    localStorage.setItem("subscription", JSON.stringify(payload.subscription));
  } else {
    localStorage.removeItem("subscription");
  }
};

export const readAuthContextFromStorage = () => {
  const userType = localStorage.getItem("user_type");
  const rolesRaw = localStorage.getItem("user_roles");
  const mappedPlayerId = localStorage.getItem("mapped_player_id");
  const viewAsPlayerId = localStorage.getItem("view_as_player_id");
  const organizationId = localStorage.getItem("organization_id");
  const organizationKind = localStorage.getItem("organization_kind");
  const subscriptionRaw = localStorage.getItem("subscription");
  let subscription = null;
  if (subscriptionRaw) {
    try { subscription = JSON.parse(subscriptionRaw); } catch (e) { subscription = null; }
  }
  let roles = [];
  if (rolesRaw) {
    try {
      const parsed = JSON.parse(rolesRaw);
      if (Array.isArray(parsed)) roles = parsed.filter(Boolean);
    } catch (e) { roles = []; }
  }
  if (roles.length === 0 && userType) {
    roles = [userType];
  }
  return {
    user_type: userType || null,
    roles,
    mapped_player_id: mappedPlayerId ? Number(mappedPlayerId) : null,
    view_as_player_id: viewAsPlayerId ? Number(viewAsPlayerId) : null,
    organization_id: organizationId ? Number(organizationId) : null,
    organization_kind: organizationKind || null,
    subscription,
  };
};

export const clearAuthContextStorage = () => {
  localStorage.removeItem("user_type");
  localStorage.removeItem("user_roles");
  localStorage.removeItem("mapped_player_id");
  localStorage.removeItem("view_as_player_id");
  localStorage.removeItem("organization_id");
  localStorage.removeItem("organization_kind");
  localStorage.removeItem("subscription");
};

export const loginUser = createAsyncThunk(
  "user/login",
  async (body, { rejectWithValue }) => {
    try {
      const response = await $apiV1.post("/auth/login",
        body
      );
      invalidateUserProfile();
      localStorage.setItem("token", response.data.token);
      persistAuthContext(response.data);
      return response.data;
    } catch (e) {
      return rejectWithValue(`${e.response.data.message}`);
    }
  }
);

export const regUser = createAsyncThunk(
  "user/registration",
  async (body, { rejectWithValue }) => {
    try {
      const response = await $apiV1.post("/auth/registration",
        body
      );
      invalidateUserProfile();
      localStorage.setItem("token", response.data.token);
      persistAuthContext(response.data);
      return response.data;
    } catch (e) {
      return rejectWithValue(`${e.response.data.message}`);
    }
  }
);

export const previewInvitation = createAsyncThunk(
  "user/previewInvitation",
  async (token, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get("/auth/invitation/preview", {
        params: { token },
      });
      return response.data;
    } catch (e) {
      const status = e.response?.status;
      const code = e.response?.data?.code;
      const message = e.response?.data?.message || "Unable to load invitation.";
      return rejectWithValue({ status, code, message });
    }
  }
);

export const acceptInvitation = createAsyncThunk(
  "user/acceptInvitation",
  async (body, { rejectWithValue }) => {
    try {
      const response = await $apiV1.post("/auth/accept-invitation", body);
      localStorage.setItem("token", response.data.token);
      persistAuthContext(response.data);
      return response.data;
    } catch (e) {
      const status = e.response?.status;
      const code = e.response?.data?.code;
      const message = e.response?.data?.message || "Unable to accept invitation.";
      return rejectWithValue({ status, code, message });
    }
  }
);

export const fetchSubscriptionStatus = createAsyncThunk(
  "user/fetchSubscriptionStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/subscription/status");
      const data = response.data?.data ?? response.data;
      if (data && typeof data === "object" && data.status) {
        localStorage.setItem("subscription", JSON.stringify(data));
      }
      return data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || "Failed to load subscription status");
    }
  }
);

export const checkSmrtExplorerAccess = createAsyncThunk(
  "user/checkSmrtExplorerAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/user/is_event_explorer_allowed");
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to check access');
    }
  }
);

export const checkAiAssistantAccess = createAsyncThunk(
  "user/checkAiAssistantAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/user/is_ai_assistant_enabled");
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to check AI assistant access');
    }
  }
);

export const checkReportDownloadAccess = createAsyncThunk(
  "user/checkReportDownloadAccess",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/user/can_download_report");
      return response.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to check report download access');
    }
  }
);

export const getFormationInfo = createAsyncThunk(
  "user/user_profile",
  async (body, { rejectWithValue }) => {
    try {
      // Shared, deduped fetch — avoids a separate network call from the many
      // components that also need the profile.
      const response = await getUserProfile();
      return response.data;
    } catch (e) {
      return rejectWithValue(`${e.response?.data?.message}`);
    }
  }
);
